/**
 * Progressive enhancements for the Editorial Sweep collection page.
 * Filtering and pagination retain normal links/forms as fallbacks.
 */

(() => {
  const wishlistKey = 'mspineapple:wishlist';
  const filterRestoreKey = 'mspineapple:collection-filter-drawer';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const normalizedFilterLabels = new Set(['size', 'material', 'color', 'colour']);
  const sizeFamilyOrder = [
    'Toddler/Baby',
    'Kids',
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'Standard/One Size',
    'Custom Size',
  ];

  const normalizeFilterText = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/centimet(?:er|re)s?/g, 'cm')
    .replace(/[^a-z0-9.]+/g, ' ')
    .trim();

  const sizeGroup = (rawLabel) => {
    const label = normalizeFilterText(rawLabel);
    if (/\b(toddler|baby|infant|newborn)\b/.test(label)) return 'Toddler/Baby';
    if (/\b(kid|kids|child|children|youth)\b/.test(label)) return 'Kids';
    if (/\b(custom|customized|bespoke|made to measure)\b/.test(label)) return 'Custom Size';
    if (/\b(one size|standard|standard size|free size)\b/.test(label)) return 'Standard/One Size';
    if (/\badult\b/.test(label)) return 'Standard/One Size';
    if (/\bxxl\b|\b2xl\b/.test(label)) return 'XXL';
    if (/\bxl\b/.test(label)) return 'XL';
    if (/\bxxs\b|\b2xs\b/.test(label)) return 'XXS';
    if (/\bxs\b/.test(label)) return 'XS';
    if (/\bs\b|\bsmall\b/.test(label)) return 'S';
    if (/\bm\b|\bmedium\b/.test(label)) return 'M';
    if (/\bl\b|\blarge\b/.test(label)) return 'L';

    const measurements = [...label.matchAll(/\b(\d{2}(?:\.\d+)?)\b/g)].map((match) => Number(match[1]));
    if (measurements.length) {
      const midpoint = measurements.reduce((sum, number) => sum + number, 0) / measurements.length;
      if (midpoint < 48) return 'Toddler/Baby';
      if (midpoint < 52) return 'Kids';
      if (midpoint < 54) return 'XS';
      if (midpoint < 56) return 'S';
      if (midpoint < 58) return 'M';
      if (midpoint < 60) return 'L';
      if (midpoint < 62) return 'XL';
      return 'XXL';
    }
    return rawLabel;
  };

  const materialRules = [
    ['Cotton', /\bcotton\b|\bcanvas\b/],
    ['Linen', /\blinen\b|\bflax\b/],
    ['Wool', /\bwool\b|\bmerino\b/],
    ['Felt', /\bfelt\b/],
    ['Straw', /\bstraw\b|\bpaper braid\b|\btoyo\b/],
    ['Raffia', /\braffia\b/],
    ['Leather', /\bleather\b/],
    ['Suede', /\bsuede\b/],
    ['Corduroy', /\bcorduroy\b/],
    ['Velvet', /\bvelvet\b|\bvelour\b/],
    ['Denim', /\bdenim\b|\bjean\b/],
    ['Polyester', /\bpolyester\b|\bpoly\b/],
    ['Cashmere', /\bcashmere\b/],
    ['Fleece', /\bfleece\b/],
    ['Knit Fabric', /\bknit\b|\bknitted\b|\bjersey\b/],
  ];

  const colorRules = [
    ['Black', /\bblack\b|\bonyx\b/],
    ['White/Cream', /\bwhite\b|\bivory\b|\boff white\b|\bcream\b/],
    ['Gray', /\bgray\b|\bgrey\b|\bcharcoal\b|\bslate\b/],
    ['Beige/Neutral', /\bbeige\b|\bsand\b|\balmond\b|\bnatural\b|\bnude\b|\boatmeal\b|\bneutral\b/],
    ['Brown', /\bbrown\b|\bcoffee\b|\bchocolate\b|\bcocoa\b|\bcognac\b|\bcaramel\b|\bmocha\b|\btaupe\b/],
    ['Khaki/Camel', /\bkhaki\b|\bcamel\b|\btan\b/],
    ['Navy', /\bnavy\b/],
    ['Blue', /\bblue\b|\bteal\b|\bturquoise\b|\baqua\b|\bcyan\b/],
    ['Red/Burgundy', /\bred\b|\bberry\b|\bburgundy\b|\bcarmine\b|\bwine\b|\bmaroon\b|\bcrimson\b|\bbrick\b/],
    ['Pink', /\bpink\b|\brose\b|\bblush\b|\bsalmon\b|\bfuchsia\b/],
    ['Orange', /\borange\b|\brust\b|\bterracotta\b|\bpeach\b/],
    ['Yellow/Mustard', /\byellow\b|\bmustard\b|\blemon\b/],
    ['Green', /\bgreen\b|\bolive\b|\bsage\b|\bmint\b|\bemerald\b|\bjade\b/],
    ['Purple', /\bpurple\b|\bplum\b|\blavender\b|\blilac\b|\bviolet\b/],
    ['Metallic', /\bgold\b|\bsilver\b|\bbronze\b|\bmetallic\b/],
    ['Multicolor', /\bmulticolor\b|\bmulti color\b|\bassorted\b|\brainbow\b/],
    ['Patterned', /\bpattern\b|\bpatterned\b|\bprint\b|\bfloral\b|\bstripe\b|\bstriped\b|\bplaid\b|\bcheck\b|\bleopard\b|\bpolka dot\b/],
  ];

  const matchingFamilies = (rawLabel, rules, fallbackFamily) => {
    const normalized = normalizeFilterText(rawLabel);
    const families = rules.filter(([, pattern]) => pattern.test(normalized)).map(([name]) => name);
    if (families.length) return families;
    return [fallbackFamily];
  };

  const filterFamilies = (filterLabel, valueLabel) => {
    const label = normalizeFilterText(filterLabel);
    if (label === 'size') return [sizeGroup(valueLabel)];
    const value = normalizeFilterText(valueLabel);
    if (label === 'material') {
      if (/\braffia\b/.test(value)) return ['Raffia'];
      if (/\bsuede\b/.test(value)) return ['Suede'];
      if (/\bvelvet\b|\bvelour\b/.test(value)) return ['Velvet'];
      if (/\bfelt\b/.test(value)) return ['Felt'];
      return matchingFamilies(valueLabel, materialRules, valueLabel);
    }
    if (/\bnavy\b/.test(value)) return ['Navy'];
    return matchingFamilies(valueLabel, colorRules, valueLabel);
  };

  const familyOrderFor = (filterLabel) => {
    const label = normalizeFilterText(filterLabel);
    if (label === 'size') return sizeFamilyOrder;
    if (label === 'material') return materialRules.map(([family]) => family);
    return colorRules.map(([family]) => family);
  };

  const readWishlist = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return new Set(Array.isArray(stored) ? stored : []);
    } catch (_error) {
      return new Set();
    }
  };

  const writeWishlist = (items) => {
    try {
      window.localStorage.setItem(wishlistKey, JSON.stringify([...items]));
    } catch (_error) {
      // localStorage can be unavailable in private browsing. The pressed
      // state still works for the current page session.
    }
  };

  const initEditorialCollection = (section) => {
    if (section.dataset.editorialCollectionBound === 'true') return;
    section.dataset.editorialCollectionBound = 'true';

    const track = section.querySelector('[data-carousel-track]');
    const cards = track ? [...track.querySelectorAll('[data-carousel-card]')] : [];
    const previous = section.querySelector('[data-carousel-prev]');
    const next = section.querySelector('[data-carousel-next]');
    let carouselIndex = 0;
    let carouselTimer;

    const getCarouselMetrics = () => {
      const cardWidth = cards[1]
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : cards[0]?.offsetWidth || 1;
      const maxIndex = Math.max(0, Math.ceil((track.scrollWidth - track.clientWidth) / Math.max(1, cardWidth)));
      return { cardWidth, maxIndex };
    };

    const syncCarousel = () => {
      if (!cards.length) return;
      const { cardWidth, maxIndex } = getCarouselMetrics();
      carouselIndex = Math.max(0, Math.min(maxIndex, Math.round(track.scrollLeft / Math.max(1, cardWidth))));
      if (previous) previous.disabled = carouselIndex === 0;
      if (next) next.disabled = carouselIndex === maxIndex;
    };

    const moveCarousel = (index) => {
      const { maxIndex } = getCarouselMetrics();
      carouselIndex = Math.max(0, Math.min(maxIndex, index));
      cards[carouselIndex]?.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      syncCarousel();
    };

    previous?.addEventListener('click', () => moveCarousel(carouselIndex - 1));
    next?.addEventListener('click', () => moveCarousel(carouselIndex + 1));
    track?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveCarousel(carouselIndex + 1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveCarousel(carouselIndex - 1);
      }
    });
    track?.addEventListener('scroll', () => {
      window.clearTimeout(carouselTimer);
      carouselTimer = window.setTimeout(syncCarousel, 80);
    }, { passive: true });
    syncCarousel();

    const filterForm = section.querySelector('[data-filter-form]');
    const filterToggle = section.querySelector('[data-filter-toggle]');
    const filterPanel = section.querySelector('[data-filter-panel]');
    const filterBackdrop = section.querySelector('[data-filter-backdrop]');
    const filterInitialFocus = section.querySelector('[data-filter-initial-focus]');
    const filterGroups = [...section.querySelectorAll('details.ec-filter-group')];
    const originalFilterGroups = [...filterGroups];
    const filterCategoryChips = [...section.querySelectorAll('[data-filter-chip-toggle]')];
    const mobileFilter = window.matchMedia('(max-width: 700px)');

    /**
     * Above this width the filters are a permanent sidebar beside the grid,
     * not an overlay. The panel is then ordinary page furniture: it never
     * hides, never traps focus, never locks the page behind a backdrop.
     */
    const inlineFilters = window.matchMedia('(min-width: 1001px)');
    let filterLastFocus = null;

    /**
     * In the drawer every group starts closed so the list stays scannable.
     * The sidebar has room to show them open, which is how the reference
     * behaves — you see the options without a click.
     */
    const syncFilterDisclosureMode = () => {
      filterGroups.forEach((group) => {
        group.open = inlineFilters.matches;
      });
    };

    const syncDrawerFilterOrder = () => {
      const priority = new Map([
        ['color', 0],
        ['colour', 0],
        ['size', 1],
        ['material', 2],
      ]);
      const originalOrder = new Map(originalFilterGroups.map((group, index) => [group, index]));
      const orderedGroups = mobileFilter.matches
        ? [...originalFilterGroups]
        : [...originalFilterGroups].sort((groupA, groupB) => {
          const rankA = priority.get(normalizeFilterText(groupA.dataset.filterLabel)) ?? 100;
          const rankB = priority.get(normalizeFilterText(groupB.dataset.filterLabel)) ?? 100;
          if (rankA !== rankB) return rankA - rankB;
          return originalOrder.get(groupA) - originalOrder.get(groupB);
        });
      filterGroups.splice(0, filterGroups.length, ...orderedGroups);
      filterGroups.forEach((group) => group.parentElement?.appendChild(group));
    };

    const enhanceNormalizedFilters = () => {
      if (!filterForm) return;
      let normalizedRawActiveCount = 0;
      let normalizedCanonicalActiveCount = 0;

      filterForm.querySelectorAll('.ec-filter-group[data-filter-label]').forEach((fieldset) => {
        const filterLabel = fieldset.dataset.filterLabel;
        if (!normalizedFilterLabels.has(normalizeFilterText(filterLabel))) return;

        const rawContainer = fieldset.querySelector('.ec-filter-group__options');
        const rawOptions = [...fieldset.querySelectorAll('[data-raw-filter-option]')]
          .map((label) => ({
            label,
            input: label.querySelector('input[type="checkbox"]'),
            valueLabel: label.dataset.filterValueLabel || '',
            count: Number(label.dataset.filterValueCount || 0),
          }))
          .filter((option) => option.input);
        if (!rawContainer || !rawOptions.length) return;

        normalizedRawActiveCount += rawOptions.filter((option) => option.input.checked).length;
        const grouped = new Map();
        rawOptions.forEach((option) => {
          filterFamilies(filterLabel, option.valueLabel).forEach((family) => {
            if (!grouped.has(family)) grouped.set(family, []);
            grouped.get(family).push(option);
          });
        });
        if (!grouped.size) return;

        const canonicalContainer = document.createElement('div');
        canonicalContainer.className = 'ec-filter-group__options ec-filter-group__options--canonical';
        canonicalContainer.dataset.canonicalFilterOptions = '';
        const canonicalInputs = new Map();
        const selectedFamilies = new Set();

        const familyOrder = familyOrderFor(filterLabel);
        const groupedEntries = [...grouped.entries()].sort(([familyA], [familyB]) => {
          const indexA = familyOrder.indexOf(familyA);
          const indexB = familyOrder.indexOf(familyB);
          if (indexA === -1 && indexB === -1) return familyA.localeCompare(familyB);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

        groupedEntries.forEach(([family, options]) => {
          if (options.some((option) => option.input.checked)) selectedFamilies.add(family);

          const optionLabel = document.createElement('label');
          optionLabel.className = 'ec-filter-option';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = selectedFamilies.has(family);
          input.disabled = options.every((option) => option.input.disabled && !option.input.checked);
          input.dataset.canonicalFilter = family;
          input.setAttribute('aria-label', `Select ${family}`);

          const name = document.createElement('span');
          // A zero-width space after the slash gives compound families like
          // "Red/Burgundy" a natural place to wrap in a narrow swatch column,
          // instead of breaking mid-word. Invisible, and the aria-label above
          // keeps the spoken text clean.
          name.textContent = family.replace(/\//g, '/\u200B');

          // Colour families get a visible sample; the checkbox stays the
          // control, the swatch is only its face.
          if (fieldset.dataset.optionLayout === 'swatch') {
            const swatch = document.createElement('span');
            swatch.className = 'ec-swatch';
            swatch.dataset.swatch = family
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '');
            swatch.setAttribute('aria-hidden', 'true');
            optionLabel.append(input, swatch, name);
          } else {
            optionLabel.append(input, name);
          }

          canonicalContainer.appendChild(optionLabel);
          canonicalInputs.set(family, input);
        });

        const syncRawInputs = () => {
          rawOptions.forEach((option) => {
            const shouldCheck = filterFamilies(filterLabel, option.valueLabel)
              .some((family) => selectedFamilies.has(family));
            option.input.checked = shouldCheck;
          });
        };

        canonicalInputs.forEach((input, family) => {
          input.addEventListener('change', () => {
            if (input.checked) selectedFamilies.add(family);
            else selectedFamilies.delete(family);
            syncRawInputs();
          });
        });

        rawContainer.before(canonicalContainer);
        // Raw Shopify parameters stay in the form and are toggled by the
        // canonical controls. They remain visible when JavaScript is absent.
        rawContainer.hidden = true;
        normalizedCanonicalActiveCount += selectedFamilies.size;

        const categoryChip = filterForm.querySelector(`[data-filter-target="${CSS.escape(fieldset.id)}"]`);
        if (categoryChip) {
          const refreshCategoryChip = () => {
            const activeCount = [...canonicalInputs.values()].filter((input) => input.checked).length;
            categoryChip.classList.toggle('is-active', activeCount > 0);
            categoryChip.replaceChildren(document.createTextNode(
              `${categoryChip.dataset.filterLabel}${activeCount ? ` (${activeCount}) ` : ' '}`,
            ));
            const symbol = document.createElement('span');
            symbol.setAttribute('aria-hidden', 'true');
            symbol.textContent = activeCount ? '×' : '+';
            categoryChip.appendChild(symbol);
          };
          canonicalInputs.forEach((input) => input.addEventListener('change', refreshCategoryChip));
          refreshCategoryChip();
        }

        const rawActiveChips = [...filterForm.querySelectorAll('[data-active-filter-chip]')]
          .filter((chip) => normalizeFilterText(chip.dataset.filterLabel) === normalizeFilterText(filterLabel));
        if (rawActiveChips.length) {
          rawActiveChips.forEach((chip) => { chip.hidden = true; });
          const selectedFilterBar = rawActiveChips[0].parentElement;
          const clearAll = selectedFilterBar?.querySelector('.ec-clear-filters');
          selectedFamilies.forEach((family) => {
            const removeButton = document.createElement('button');
            removeButton.type = 'button';
            removeButton.className = 'ec-filter-chip';
            removeButton.setAttribute('aria-label', `Remove ${filterLabel}: ${family}`);
            const buttonText = document.createElement('span');
            buttonText.textContent = family;
            const closeSymbol = document.createElement('span');
            closeSymbol.setAttribute('aria-hidden', 'true');
            closeSymbol.textContent = '×';
            removeButton.append(buttonText, closeSymbol);
            removeButton.addEventListener('click', () => {
              const canonicalInput = canonicalInputs.get(family);
              if (!canonicalInput) return;
              canonicalInput.checked = false;
              canonicalInput.dispatchEvent(new Event('change', { bubbles: true }));
            });
            selectedFilterBar?.insertBefore(removeButton, clearAll || null);
          });
        }
      });

      const activeTotal = Number(filterForm.dataset.activeFilterCount || 0)
        - normalizedRawActiveCount
        + normalizedCanonicalActiveCount;
      const activeTotalText = section.querySelector('[data-active-filter-total]');
      const refreshActiveTotal = () => {
        if (!activeTotalText) return;
        const checkedCanonical = filterForm.querySelectorAll('[data-canonical-filter]:checked').length;
        const nextTotal = activeTotal - normalizedCanonicalActiveCount + checkedCanonical;
        activeTotalText.textContent = nextTotal > 0 ? ` (${nextTotal})` : '';
      };
      filterForm.querySelectorAll('[data-canonical-filter]').forEach((input) => {
        input.addEventListener('change', refreshActiveTotal);
      });
      refreshActiveTotal();
    };

    enhanceNormalizedFilters();
    syncDrawerFilterOrder();
    syncFilterDisclosureMode();

    const getFilterFocusable = () => filterPanel
      ? [...filterPanel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [data-filter-initial-focus], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element.getClientRects().length)
      : [];

    const syncFilterDialogSemantics = () => {
      if (!filterPanel) return;
      // A sidebar is not a dialog: announcing it as a modal would tell screen
      // readers the rest of the page is inert when it plainly is not.
      if (!filterPanel.hidden && mobileFilter.matches) {
        filterPanel.setAttribute('role', 'dialog');
        filterPanel.setAttribute('aria-modal', 'true');
        filterPanel.setAttribute('aria-labelledby', filterInitialFocus?.id || '');
      } else {
        filterPanel.removeAttribute('role');
        filterPanel.removeAttribute('aria-modal');
        filterPanel.removeAttribute('aria-labelledby');
      }
    };

    const closeFilters = ({ restoreFocus = true } = {}) => {
      if (!filterPanel) return;
      // The sidebar has nothing to close.
      if (inlineFilters.matches) return;
      if (filterPanel.hidden) return;
      filterPanel.hidden = true;
      if (filterBackdrop) filterBackdrop.hidden = true;
      filterToggle?.setAttribute('aria-expanded', 'false');
      filterCategoryChips.forEach((chip) => chip.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.removeProperty('overflow');
      syncFilterDialogSemantics();
      if (restoreFocus) filterLastFocus?.focus();
    };

    const openFilters = (targetSelector, expandedChip = null) => {
      if (!filterPanel) return;
      filterLastFocus = document.activeElement;
      filterPanel.hidden = false;
      filterToggle?.setAttribute('aria-expanded', 'true');
      filterCategoryChips.forEach((chip) => {
        chip.setAttribute('aria-expanded', String(chip === expandedChip));
      });
      syncFilterDialogSemantics();
      filterGroups.forEach((group) => { group.open = false; });
      const target = targetSelector ? filterPanel.querySelector(`#${CSS.escape(targetSelector)}`) : null;
      if (target) target.open = true;
      if (filterBackdrop && mobileFilter.matches) filterBackdrop.hidden = false;
      if (mobileFilter.matches) document.documentElement.style.overflow = 'hidden';
      window.requestAnimationFrame(() => {
        if (mobileFilter.matches || !target) {
          filterInitialFocus?.focus();
          target?.scrollIntoView({ block: 'start' });
        } else {
          (target?.querySelector('input:not([disabled])') || target)?.focus();
        }
      });
    };

    /**
     * As a full-screen overlay the panel covers its own backdrop, so touch users
     * had no way out — only Escape or submitting the filter form. Built here rather than in
     * Liquid so the fix stays inside the JS/CSS assets.
     */
    const filterHead = filterPanel?.querySelector('.ec-filter-panel__head');
    if (filterHead && !filterHead.querySelector('.ec-filter-panel__close')) {
      const filterClose = document.createElement('button');
      filterClose.type = 'button';
      filterClose.className = 'ec-filter-panel__close';
      filterClose.setAttribute('aria-label', 'Close filters');
      filterClose.innerHTML = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" stroke-linecap="round"/></svg>';
      filterClose.addEventListener('click', () => closeFilters());

      const headActions = document.createElement('div');
      headActions.className = 'ec-filter-panel__head-actions';
      const clearLink = filterHead.querySelector('.ec-filter-panel__clear');
      if (clearLink) headActions.appendChild(clearLink);
      headActions.appendChild(filterClose);
      filterHead.appendChild(headActions);
    }

    filterToggle?.addEventListener('click', () => {
      if (filterPanel?.hidden) openFilters();
      else closeFilters();
    });
    filterCategoryChips.forEach((chip) => {
      chip.addEventListener('click', () => openFilters(chip.dataset.filterTarget, chip));
    });
    filterBackdrop?.addEventListener('click', () => closeFilters());
    /**
     * Only intercept Escape while the panel is an actual overlay.
     *
     * Above the breakpoint the panel is a permanent sidebar, so `hidden` is
     * always false and this handler used to swallow every Escape on the page
     * — including the one that should have dismissed the cart drawer, the
     * quick view, or favorites. Closing a permanent sidebar is meaningless
     * anyway: syncInlineFilters re-shows it immediately.
     */
    const filtersAreOverlay = () => Boolean(filterPanel) && !filterPanel.hidden && !inlineFilters.matches;

    /**
     * Only below 701px is the panel a real modal layer. Between 701 and 1000 it
     * is an in-flow disclosure beside the grid, and trapping Tab there stranded
     * keyboard users inside the filter list with no way back to the products.
     */
    const filtersAreModal = () => Boolean(filterPanel) && !filterPanel.hidden && mobileFilter.matches;

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && filtersAreOverlay()) {
        event.preventDefault();
        closeFilters();
      }
      if (event.key === 'Tab' && filtersAreModal()) {
        const focusable = getFilterFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
    /**
     * Keep the panel's state honest for the current layout. As a sidebar it is
     * always shown and the page never scroll-locks; as an overlay it starts
     * closed. Runs on load and whenever the viewport crosses the breakpoint,
     * so resizing mid-session cannot strand a locked page or a hidden sidebar.
     */
    const syncInlineFilters = () => {
      if (!filterPanel) return;
      if (inlineFilters.matches) {
        filterPanel.hidden = false;
        if (filterBackdrop) filterBackdrop.hidden = true;
        document.documentElement.style.removeProperty('overflow');
      } else if (!filterPanel.hidden) {
        filterPanel.hidden = true;
        filterToggle?.setAttribute('aria-expanded', 'false');
        if (filterBackdrop) filterBackdrop.hidden = true;
        document.documentElement.style.removeProperty('overflow');
      }
      syncFilterDialogSemantics();
    };

    inlineFilters.addEventListener('change', syncInlineFilters);
    syncInlineFilters();

    try {
      const restoredFilter = JSON.parse(window.sessionStorage.getItem(filterRestoreKey) || 'null');
      window.sessionStorage.removeItem(filterRestoreKey);
      if (
        restoredFilter?.path === window.location.pathname
        && restoredFilter.expires > Date.now()
        && !inlineFilters.matches
      ) {
        openFilters(restoredFilter.target || '');
      }
    } catch (_error) {
      // sessionStorage can be unavailable in private browsing. Filtering
      // still submits automatically; only drawer restoration is skipped.
    }

    mobileFilter.addEventListener('change', () => {
      syncDrawerFilterOrder();
      syncFilterDisclosureMode();
      if (filterPanel && !filterPanel.hidden && !inlineFilters.matches) {
        if (filterBackdrop) filterBackdrop.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      }
      syncFilterDialogSemantics();
    });

    const filterStatus = section.querySelector('[data-filter-status]');
    let filterSubmitTimer;

    const preserveOpenFilter = () => {
      if (!filtersAreOverlay()) return;
      const openGroup = filterGroups.find((group) => group.open);
      try {
        window.sessionStorage.setItem(filterRestoreKey, JSON.stringify({
          path: window.location.pathname,
          target: openGroup?.id || '',
          expires: Date.now() + 15000,
        }));
      } catch (_error) {
        // Native navigation remains the reliable fallback.
      }
    };

    const showFilterLoading = () => {
      window.clearTimeout(filterSubmitTimer);
      section.classList.add('is-filtering');
      section.setAttribute('aria-busy', 'true');
      filterForm.setAttribute('aria-busy', 'true');
      if (filterStatus) filterStatus.textContent = 'Updating products…';
    };

    const queueFilterUpdate = () => {
      window.clearTimeout(filterSubmitTimer);
      filterSubmitTimer = window.setTimeout(() => filterForm.requestSubmit(), 260);
    };

    filterForm?.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
      if (control instanceof HTMLInputElement && !control.checkValidity()) return;
      queueFilterUpdate();
    });

    filterForm?.addEventListener('submit', () => {
      preserveOpenFilter();
      showFilterLoading();
    });

    filterPanel?.addEventListener('click', (event) => {
      const target = event.target instanceof Element
        ? event.target.closest('a.ec-filter-panel__clear')
        : null;
      if (!target || !filterPanel.contains(target)) return;
      preserveOpenFilter();
      showFilterLoading();
    });

    let wishlist = readWishlist();

    /**
     * The favorites drawer can remove an item too, which would leave this
     * closure holding a stale set. Re-read whenever anything announces a
     * write — including our own, which keeps the two paths identical.
     */
    document.addEventListener('wishlist:updated', () => {
      wishlist = readWishlist();
    });

    const bindWishlistButtons = (scope = section) => {
      scope.querySelectorAll('[data-wishlist-button]').forEach((button) => {
        if (button.dataset.wishlistBound === 'true') return;
        button.dataset.wishlistBound = 'true';
        const handle = button.dataset.productHandle;
        const saved = wishlist.has(handle);
        button.setAttribute('aria-pressed', String(saved));
        if (saved) button.setAttribute('aria-label', button.getAttribute('aria-label').replace(/^Save /, 'Remove '));

        button.addEventListener('click', () => {
          const isSaved = wishlist.has(handle);
          if (isSaved) wishlist.delete(handle);
          else wishlist.add(handle);
          writeWishlist(wishlist);
          button.setAttribute('aria-pressed', String(!isSaved));
          const label = button.getAttribute('aria-label');
          button.setAttribute('aria-label', isSaved ? label.replace(/^Remove /, 'Save ') : label.replace(/^Save /, 'Remove '));
          document.dispatchEvent(
            new CustomEvent('wishlist:updated', {
              detail: { handle, saved: !isSaved, source: 'card' },
            })
          );
        });
      });
    };
    bindWishlistButtons();
    // Infinite-scroll owns pagination. Rebind the only card-local behavior
    // that is not event-delegated whenever it appends another product page.
    section.addEventListener('msp:products:appended', (event) => {
      bindWishlistButtons(event.detail?.container || section);
    });
  };

  document.querySelectorAll('[data-editorial-collection]').forEach(initEditorialCollection);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches?.('[data-editorial-collection]')) {
      initEditorialCollection(event.target);
    }
    event.target.querySelectorAll?.('[data-editorial-collection]').forEach(initEditorialCollection);
  });
})();
